// Quick View Popup JavaScript
class QuickViewPopup {
  constructor() {
    this.popup = null;
    this.overlay = null;
    this.content = null;
    this.currentProduct = null;
    this.quantity = 1;
    this.selectedVariant = null;
    this.productVariants = [];
    this.productImages = [];
    this.currentImageIndex = 0;

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setupPopup());
    } else {
      this.setupPopup();
    }
  }

  setupPopup() {
    this.popup = document.getElementById("quick-view-popup");
    this.overlay = this.popup?.querySelector(".quick-view-overlay");
    this.content = this.popup?.querySelector(".quick-view-product");

    if (!this.popup || !this.overlay || !this.content) {
      console.error("Quick View Popup elements not found");
      return;
    }

    // Setup event listeners
    this.overlay.addEventListener("click", () => this.close());

    // Handle escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.popup.classList.contains("active")) {
        this.close();
      }
    });

    // Handle click outside to close
    document.addEventListener("click", (e) => {
      if (this.popup.classList.contains("active")) {
        const isClickInsidePopup = this.content.contains(e.target);
        const isClickOnOverlay = this.overlay.contains(e.target);

        if (!isClickInsidePopup && !isClickOnOverlay) {
          this.close();
        }
      }
    });

    if (
      typeof PUB_SUB_EVENTS !== "undefined" &&
      typeof subscribe !== "undefined"
    ) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.close();
      });
    }
  }

  async open(triggerButton) {
    if (!triggerButton) return;

    let inventories = {};
    try {
      const invData = triggerButton.dataset.productInventory;
      if (invData) {
        const txt = document.createElement("textarea");
        txt.innerHTML = invData;
        inventories = JSON.parse(txt.value);
      }
    } catch (e) {
      console.error("Error parsing inventory:", e);
    }

    const productData = {
      id: triggerButton.dataset.productId,
      url: triggerButton.dataset.productUrl,
      title: triggerButton.dataset.productTitle,
      price: triggerButton.dataset.productPrice,
      comparePrice: triggerButton.dataset.productComparePrice,
      image: triggerButton.dataset.productImage,
      variantId: triggerButton.dataset.productVariantId,
      inventories: inventories,
    };

    this.currentProduct = productData;
    this.quantity = 1;

    // Fetch product variants
    await this.fetchProductVariants(productData.url);

    this.renderProduct(productData);
    this.show();
  }

  close() {
    this.hide();
  }

  async fetchProductVariants(productUrl) {
    try {
      // Remove query parameters from the URL before appending .js
      const baseUrl = productUrl.split('?')[0];
      const response = await fetch(baseUrl + ".js");
      const productData = await response.json();

      // Store description as plain text
      if (productData.description) {
        const doc = new DOMParser().parseFromString(
          productData.description,
          "text/html",
        );
        this.currentProduct.description = doc.body.textContent || "";
      }

      // Merge inventory quantities into product variants
      this.productVariants = productData.variants.map((variant) => ({
        ...variant,
        inventory_quantity:
          this.currentProduct.inventories[variant.id] !== undefined
            ? this.currentProduct.inventories[variant.id]
            : null,
      }));

      // Extract all product images
      this.productImages = productData.images.map((img) => ({
        src: img,
        alt: productData.title,
      }));

      // Set selected variant to the first available one
      this.selectedVariant =
        this.productVariants.find((v) => v.available) ||
        this.productVariants[0];

      // Reset image index
      this.currentImageIndex = 0;
    } catch (error) {
      console.error("Error fetching product variants:", error);
      this.productVariants = [];
      this.selectedVariant = null;
      this.productImages = [];
    }
  }

  show() {
    this.popup.style.display = "flex";
    // Force reflow
    this.popup.offsetHeight;
    this.popup.classList.add("active");
    document.body.style.overflow = "hidden";
  }

  hide() {
    this.popup.classList.remove("active");
    setTimeout(() => {
      this.popup.style.display = "none";
      document.body.style.overflow = "";
    }, 300);
  }

  renderProduct(product) {
    const template = document.getElementById("quick-view-product-template");
    if (!template) {
      console.error("Quick view template not found");
      return;
    }

    const clone = template.content.cloneNode(true);

    // Set product data
    const imageTrack = clone.querySelector(".image-slider-track");
    const productTitle = clone.querySelector(".product-title");
    const currentPrice = clone.querySelector(".current-price");
    const originalPrice = clone.querySelector(".original-price");
    const savings = clone.querySelector(".savings");
    const sizeSelector = clone.querySelector(".size-selector");
    const sizeOptions = clone.querySelector(".size-options");
    const selectedSizeLabel = clone.querySelector(".selected-size");
    const description = clone.querySelector(".product-description p");
    const viewDetailsLink = clone.querySelector(".view-details-link");
    const stockInfo = clone.querySelector(".stock-info");
    const variantId = clone.querySelector(".variant-id");
    const quantityInput = clone.querySelector(".quantity-input");

    if (imageTrack) {
      imageTrack.innerHTML = "";
      if (this.productImages.length > 0) {
        this.productImages.forEach((img) => {
          const imgEl = document.createElement("img");
          imgEl.src = img.src;
          imgEl.alt = img.alt;
          imgEl.className = "product-image";
          imageTrack.appendChild(imgEl);
        });
      } else {
        const imgEl = document.createElement("img");
        imgEl.src = product.image;
        imgEl.alt = product.title;
        imgEl.className = "product-image";
        imageTrack.appendChild(imgEl);
      }

      imageTrack.style.transform = "translateX(0)";

      // Add image-loaded class after a small delay to trigger initial animation
      setTimeout(() => {
        const images = imageTrack.querySelectorAll(".product-image");
        images.forEach((img) => img.classList.add("image-loaded"));
      }, 100);
    }
    if (productTitle) productTitle.textContent = product.title;
    if (currentPrice)
      currentPrice.textContent = this.formatMoney(product.price);
    if (variantId)
      variantId.value = this.selectedVariant?.id || product.variantId;
    if (quantityInput) quantityInput.value = this.quantity;

    // Handle size selector
    if (sizeSelector && sizeOptions && this.productVariants.length > 0) {
      this.renderSizeOptions(sizeOptions, selectedSizeLabel);
      sizeSelector.style.display = "block";
    } else if (sizeSelector) {
      sizeSelector.style.display = "none";
    }

    // Handle compare price and savings
    if (
      product.comparePrice &&
      parseFloat(product.comparePrice) > parseFloat(product.price)
    ) {
      if (originalPrice)
        originalPrice.textContent = this.formatMoney(product.comparePrice);
      if (originalPrice) originalPrice.style.display = "inline";

      const savingsAmount =
        parseFloat(product.comparePrice) - parseFloat(product.price);
      const savingsPercent = Math.round(
        (savingsAmount / parseFloat(product.comparePrice)) * 100,
      );
      if (savings) {
        savings.textContent = `YOU SAVE ${this.formatMoney(savingsAmount)}`;
        savings.style.display = "inline-block";
      }
    } else {
      if (originalPrice) originalPrice.style.display = "none";
      if (savings) savings.style.display = "none";
    }

    // Product description
    if (description) {
      if (product.description) {
        description.textContent = product.description;
      } else {
        description.textContent = "";
      }
    }

    // View details link
    if (viewDetailsLink) {
      viewDetailsLink.href = product.url;
    }

    // Stock information based on selected variant
    if (stockInfo && this.selectedVariant) {
      const stockCount = this.selectedVariant.inventory_quantity;
      console.log(this.productVariants);

      if (stockCount != null && stockCount > 0) {
        stockInfo.textContent = `${stockCount} in stock`;
        stockInfo.style.color = "#28a745";
      } else if (this.selectedVariant.available) {
        stockInfo.textContent = "In stock";
        stockInfo.style.color = "#28a745";
      } else {
        stockInfo.textContent = "Out of stock";
        stockInfo.style.color = "#dc3545";
      }
    }

    // Clear existing content and append new content
    this.content.innerHTML = "";
    this.content.appendChild(clone);
  }

  renderSizeOptions(sizeOptionsContainer, selectedSizeLabel) {
    sizeOptionsContainer.innerHTML = "";

    // Extract unique sizes from variants
    const sizes = [
      ...new Set(
        this.productVariants.map((variant) => variant.option1 || "One Size"),
      ),
    ];

    sizes.forEach((size) => {
      const sizeOption = document.createElement("button");
      sizeOption.className = "size-option";
      sizeOption.textContent = size;
      sizeOption.dataset.size = size;

      // Check if this size is available
      const availableVariant = this.productVariants.find(
        (v) => (v.option1 || "One Size") === size && v.available,
      );

      if (!availableVariant) {
        sizeOption.classList.add("out-of-stock");
      } else {
        sizeOption.classList.add("available");
      }

      // Set selected state
      if (
        this.selectedVariant &&
        (this.selectedVariant.option1 || "One Size") === size
      ) {
        sizeOption.classList.add("selected");
        if (selectedSizeLabel) {
          selectedSizeLabel.textContent = size;
        }
      }

      // Add click handler
      sizeOption.addEventListener("click", () =>
        this.selectSize(size, availableVariant),
      );

      sizeOptionsContainer.appendChild(sizeOption);
    });
  }

  selectSize(size, variant) {
    if (!variant) return; // Don't select out of stock sizes

    // Update selected variant
    this.selectedVariant = variant;

    // Update UI
    if (this.content) {
      this.content.querySelectorAll(".size-option").forEach((option) => {
        option.classList.remove("selected");
        if (option.dataset.size === size) {
          option.classList.add("selected");
        }
      });

      // Update selected size label
      const selectedSizeLabel = this.content.querySelector(".selected-size");
      if (selectedSizeLabel) {
        selectedSizeLabel.textContent = size;
      }

      // Update variant ID in form
      const variantIdInput = this.content.querySelector(".variant-id");
      if (variantIdInput) {
        variantIdInput.value = variant.id;
      }

      // Update price if different
      const currentPriceElement = this.content.querySelector(".current-price");
      if (currentPriceElement && variant.price) {
        currentPriceElement.textContent = this.formatMoney(variant.price);
      }

      // Update stock info
      const stockInfo = this.content.querySelector(".stock-info");
      if (stockInfo) {
        const stockCount = variant.inventory_quantity;
        if (stockCount != null && stockCount > 0) {
          stockInfo.textContent = `${stockCount} in stock`;
          stockInfo.style.color = "#28a745";
        } else if (variant.available) {
          stockInfo.textContent = "In stock";
          stockInfo.style.color = "#28a745";
        } else {
          stockInfo.textContent = "Out of stock";
          stockInfo.style.color = "#dc3545";
        }
      }
    }
  }

  formatMoney(cents) {
    // Format money based on Shopify's money format
    const amount = cents / 100;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  updateQuantity(change) {
    const input = this.content?.querySelector(".quantity-input");
    if (!input) return;

    const newValue = parseInt(input.value) + change;
    const minValue = parseInt(input.min) || 1;
    const maxValue = parseInt(input.max) || 10;

    if (newValue >= minValue && newValue <= maxValue) {
      this.quantity = newValue;
      input.value = newValue;
    }
  }

  updateQuantityFromInput() {
    const input = this.content?.querySelector(".quantity-input");
    if (!input) return;

    const minValue = parseInt(input.min) || 1;
    const maxValue = parseInt(input.max) || 10;
    let newValue = parseInt(input.value);

    // Validate the input value
    if (isNaN(newValue) || newValue < minValue) {
      newValue = minValue;
    } else if (newValue > maxValue) {
      newValue = maxValue;
    }

    this.quantity = newValue;
    input.value = newValue;
  }

  navigateImage(direction) {
    if (this.productImages.length <= 1) return;
    if (this.isAnimating) return;

    const imageTrack = this.content?.querySelector(".image-slider-track");
    if (!imageTrack) return;

    this.isAnimating = true;

    if (direction === 1) {
      // Next
      imageTrack.style.transition = "transform 0.4s ease-in-out";
      imageTrack.style.transform = "translateX(-100%)";

      setTimeout(() => {
        imageTrack.appendChild(imageTrack.firstElementChild);
        imageTrack.style.transition = "none";
        imageTrack.style.transform = "translateX(0)";
        this.isAnimating = false;
      }, 400);
    } else if (direction === -1) {
      // Prev
      imageTrack.insertBefore(
        imageTrack.lastElementChild,
        imageTrack.firstElementChild,
      );
      imageTrack.style.transition = "none";
      imageTrack.style.transform = "translateX(-100%)";

      // Force reflow
      imageTrack.offsetHeight;

      imageTrack.style.transition = "transform 0.4s ease-in-out";
      imageTrack.style.transform = "translateX(0)";

      setTimeout(() => {
        this.isAnimating = false;
      }, 400);
    }
  }
}

window.openQuickView = function (triggerButton) {
  if (!window.quickViewPopup) {
    window.quickViewPopup = new QuickViewPopup();
  }
  window.quickViewPopup.open(triggerButton);
};

window.closeQuickView = function () {
  if (window.quickViewPopup) {
    window.quickViewPopup.close();
  }
};

window.updateQuantity = function (change) {
  if (window.quickViewPopup) {
    window.quickViewPopup.updateQuantity(change);
  }
};

window.navigateImage = function (direction) {
  if (window.quickViewPopup) {
    window.quickViewPopup.navigateImage(direction);
  }
};

window.updateQuantityFromInput = function () {
  if (window.quickViewPopup) {
    window.quickViewPopup.updateQuantityFromInput();
  }
};

// Initialize the popup when the page loads
document.addEventListener("DOMContentLoaded", () => {
  window.quickViewPopup = new QuickViewPopup();
});
